import { useCallback, useContext, useState } from "react";
import { ModalConfig, ModalContext } from "./ModalContext";

interface ModalState {
  isOpen: boolean;
  config: ModalConfig | null;
}

interface UseModalReturn {
  isOpen: boolean;
  config: ModalConfig | null;
  openModal: (config: ModalConfig) => void;
  closeModal: () => void;
}

/**
 * Hook to control modals with both global and local state support
 *
 * @param useLocal - If true, uses local state instead of global context
 * @returns Modal control methods and state
 *
 * @example
 * // Global modal (can be triggered from anywhere)
 * const { openModal } = useModal();
 * openModal({
 *   title: "Confirm Delete",
 *   content: "Are you sure?",
 *   onConfirm: () => deleteItem()
 * });
 *
 * @example
 * // Local modal (component-specific)
 * const { openModal, isOpen, config } = useModal(true);
 * return (
 *   <>
 *     <button onClick={() => openModal({...})}>Open</button>
 *     {isOpen && <Modal {...config} />}
 *   </>
 * );
 */
export const useModal = (useLocal: boolean = false): UseModalReturn => {
  const context = useContext(ModalContext);

  // Local state for component-specific modals
  const [localState, setLocalState] = useState<ModalState>({
    isOpen: false,
    config: null,
  });

  const openModal = useCallback(
    (config: ModalConfig) => {
      if (useLocal) {
        setLocalState({
          isOpen: true,
          config,
        });
      } else {
        if (!context) {
          console.error("useModal: ModalProvider not found in tree. Wrap your app with ModalProvider.");
          return;
        }
        context.openModal(config);
      }
    },
    [useLocal, context]
  );

  const closeModal = useCallback(() => {
    if (useLocal) {
      setLocalState((prev) => ({
        ...prev,
        isOpen: false,
      }));
      setTimeout(() => {
        setLocalState({
          isOpen: false,
          config: null,
        });
      }, 200);
    } else {
      if (!context) {
        console.error("useModal: ModalProvider not found in tree.");
        return;
      }
      context.closeModal();
    }
  }, [useLocal, context]);

  if (useLocal) {
    return {
      isOpen: localState.isOpen,
      config: localState.config,
      openModal,
      closeModal,
    };
  }

  if (!context) {
    console.error("useModal: ModalProvider not found in tree. Wrap your app with ModalProvider.");
    return {
      isOpen: false,
      config: null,
      openModal: () => {},
      closeModal: () => {},
    };
  }

  return {
    isOpen: context.isOpen,
    config: context.config,
    openModal,
    closeModal,
  };
};
